import { WorkAPI } from '../API'
import { collection } from '../../infra/collection'
import { Insight } from './Insight'
import { Id } from '../../types/definitions'
import {
  GetJobInsightsQueryRequest,
  GetLatestJobInsightsRequest,
  GenerateJobInsightsRequest,
  Insight as InsightProto,
  GetJobInsightsQueryResponse,
} from '@taxfyle/api-internal/internal/insights_pb'
import { InsightPropsDto, QueryInsightPropsDto } from './dtos'
import { QueryInsight } from './QueryInsight'

export class InsightStore {
  /**
   * Insights for jobs.
   */
  jobInsights = collection<InsightPropsDto, Insight>({
    getDataId: (i) => i.insightId,
    getModelId: (i) => i.id,
    create: () => new Insight(),
  })

  /**
   * Query Insight for job.
   */
  jobQueryInsight = collection<QueryInsightPropsDto, QueryInsight>({
    getDataId: (i) => i.job_id,
    getModelId: (i) => i.jobId,
    create: () => new QueryInsight(),
  })

  /**
   * Constructor.
   */
  constructor(private readonly api: WorkAPI) {}

  /**
   * Get all the latest insights in the collection for the given job ID.
   * @param jobId
   */
  latestInsightsForJob(jobId: string) {
    return this.jobInsights.filter((i) => i.jobId === jobId)
  }

  /**
   * Find the query insight in the collection for the given job ID.
   * @param jobId
   */
  queryInsightForJob(jobId: string) {
    return this.jobQueryInsight.find((i) => i.jobId === jobId)
  }

  /**
   * Fetches basic Insights for the given job ID.
   * @param jobId
   */
  async fetchJobQueryInsights(jobId: Id): Promise<void> {
    const request = new GetJobInsightsQueryRequest()
    request.setJobId(Id.assert(jobId))

    const res = await this.api.insights.getJobInsightsQuery(request)
    const obj = res.toObject()

    this.setJobQueryInsights(jobId, obj)
  }

  /**
   * Fetches the latest Insights for the given job ID.
   * @param jobId
   */
  async fetchLatestJobInsights(jobId: Id): Promise<void> {
    const request = new GetLatestJobInsightsRequest()
    request.setJobId(Id.assert(jobId))

    const res = await this.api.insights.getLatestJobInsights(request)
    const obj = res.toObject()

    this.clearJobInsightsForJobId(jobId)
    this.setJobInsights(jobId, obj.insightsList)
  }

  /**
   * Generate all Insights or refresh all Insights for the given job ID.
   * @param jobId
   */
  async generateOrRefreshJobInsights(jobId: Id): Promise<void> {
    const request = new GenerateJobInsightsRequest()
    request.setJobId(Id.assert(jobId))
    // Currently we only offer 5 insights in total, so lets just return all of them.
    request.setPageSize(5)

    await this.api.insights.generateJobInsights(request)

    await this.fetchLatestJobInsights(jobId)
  }

  /**
   * Set the Job Insights to the collection.
   * @param jobId
   * @param insights
   */
  private setJobInsights(jobId: Id, insights: InsightProto.AsObject[]) {
    insights.forEach((insight) => {
      this.jobInsights.set({ jobId, ...insight })
    })
  }

  /**
   * Set the Job Query Insights to the collection.
   * @param jobId
   * @param insights
   */
  private setJobQueryInsights(
    jobId: Id,
    queryInsights: GetJobInsightsQueryResponse.AsObject,
  ) {
    this.jobQueryInsight.set({
      job_id: jobId,
      periods_of_unresponsiveness_towards_client:
        queryInsights.periodsOfUnresponsivenessTowardsClient,
      count_of_total_final_returns: queryInsights.countOfTotalFinalReturns,
      count_of_total_draft_returns: queryInsights.countOfTotalDraftReturns,
    })
  }

  /**
   * Clears all the Job Insights for the given job ID.
   * @param jobId
   */
  private clearJobInsightsForJobId(jobId: Id): void {
    this.jobInsights
      .filter((i) => i.jobId === jobId)
      .forEach((insight) => {
        this.jobInsights.remove(insight)
      })
  }
}
